import { computed, effect, EffectRef, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { Configuration } from '../../models/interfaces/configuration/index.interface';
import { SearchOption } from '../../models/types/components/Search.type';
import { AppViews } from '../../models/enums/dvm/DvmViews.enum';
import { DvmService } from '../dvm.service';
import { UiService } from '../ui.service';
import { SearchTypes } from '../../models/enums/components/Search/SearchTypes.enum';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { SeatmapDisplay } from '@models/types/components/SeatmapDisplay.type';
import { MenuService } from './menu.service';
import { AmenitiesService } from '@services/amenities.service';
import { isInteriorViewId } from '@helpers/utils/dvm-node.util';

@Injectable()
export class SearchService {

  // SERVICES
  public readonly  dvmService:        DvmService                             = inject(DvmService);                          // DVM SERVICE
  private readonly config:            Signal<Configuration['Ui']>            = inject(UiService).uiConfig;                  // UI CONFIGURATION
  private readonly menuService:       MenuService                            = inject(MenuService);
  private readonly amsService:        AmenitiesService                       = inject(AmenitiesService);


  // SEARCH CONFIGURATION
  public readonly searchConfig:       Signal<Configuration['Ui']['Search']>  = computed((): Configuration['Ui']['Search'] => this.config()['Search']); // SEARCH CONFIGURATION
  private readonly seatmapDisplay:    Signal<SeatmapDisplay> = computed((): SeatmapDisplay=> this.config().SeatmapDisplay);
  private readonly view3dDisplayEnabled:  Signal<boolean>         = computed((): boolean => this.config().View3dDisplay.enabled);


  // SEARCH STATE
  public readonly searchActive:       WritableSignal<boolean | null>         = signal(null);          // Estado del Search (Abierto/Cerrado)
  public readonly searchValue:        WritableSignal<SearchOption| null>     = signal(null);          // Valor del Search (Normalmente una Search Option con label y value)

  private readonly gaExceptions:          Signal<GeneralAdmission> = computed((): GeneralAdmission=> this.config().GeneralAdmission);


  // GLOBAL STATE
  public readonly view:               Signal<AppViews|null>                  = this.dvmService.view;  // Vista actual del DVM
  public readonly isAnimating:        WritableSignal<boolean>                = signal(false);         // Estado de la animación del Search
  public readonly searchVisible:      WritableSignal<boolean>                = signal(false);         // Search ocupando espacio en el layout (abierto o animando salida)
  public readonly duration:           number                                 = 300;                   // Duración de la animación del Search                  
  

  // SEARCH METHODS
  
  /**
   * Método básico del servicio para activar o desactivar el Search. 
   * Si se llama mientras se está animando, no se ejecutará.
   * @returns {void}
   */
  public toggleSearch(): void {

    if(this.isAnimating()) return;

    this.searchActive.update(val => !val);

  }

  /**
   * Método que establece el valor de SearchValue.
   * Este valor puede ser un Search Option o un valor null a modo reset.
   * Útil para guardar el valor de la búsqueda en el servicio.
   * 
   * @param {SearchOption | null} value 
   */
  public setSearchValue(value: SearchOption | null): void {

    this.searchValue.set(value); 

  }

  /**
   * Método principal del buscador por el cual navega a la sección buscada.
   * El método acepta un SearchOption o un string como parámetro. 
   * Cambiará el estado del Search a cerrado y retorna el observable de la carga del mapa para
   * poder suscribirse a él y realizar acciones adicionales.
   * 
   * @param {SearchOption | string} search 
   * @returns {Observable<MapViewerLoadObject>}
   */
  public navigateToSearch(search: SearchOption | string, nodeId?: string): void {
    
    const searchValue: string = Object.prototype.hasOwnProperty.call(search, 'value') ? (search as SearchOption).value : search as string;
    const isIntSection = isInteriorViewId(searchValue);
    this.toggleSearch();  

    
    // If the selected item is not related to the searched section, clear the selection
    const itemSelected = this.menuService.itemSelected();
    if (
      itemSelected &&
      itemSelected.relatedSeats &&
      itemSelected.relatedSeats.section &&
      !itemSelected.relatedSeats.section.includes(searchValue)
    ) {
      this.menuService.clearCurrentSelection();
    }

    if (this.dvmService.selectNodeInTopview(searchValue)) { //stay in Top view id both views are disabled or 3d disabled + is exception
      this.dvmService.viewer.unselectAll();
      this.dvmService.viewer.select(searchValue);      
      return;
    }

    if (isIntSection || this.dvmService.apply3DView(searchValue)) {

      this.dvmService.load3dView(searchValue).subscribe({
        error: (): void => undefined
      }); //load 3d view directly if includes  (INT), (INT)R, or (INT)E OR is exception
    
    } else if( this.view() === AppViews.Main ){

      this.dvmService.viewer.goTo(searchValue);

    } else {
      if(this.seatmapDisplay().enabled) {

        this.dvmService.loadMap(searchValue).subscribe({
          next: (() => {
            this.amsService.checkSectionAmenity(this.dvmService.viewer.getNodeById(this.dvmService.currentMap!)!.original_id);
            //if advanced search, select seat
            if(nodeId) {
              this.dvmService.viewer.select(nodeId);
              window.setTimeout((): void => {
                this.dvmService.viewer.goTo(nodeId!);
              }, this.duration + 250);
            }
          }),
          error: (error: unknown): void => console.error('Error navigating to search result', error)
        });
      }


    }
            
  }

  /**
   * Selecciona la sección o asiento buscado en el DVM.
   * Para ello, se basa en la vista actual del DVM y el tipo de searcher que se esté utilizando.
   * 
   * Si el search es simple, el valor de búsqueda es el value del SearchOption.
   * 
   * Si es avanzado, el valor de búsqueda varía ya que en el buscador avanzado guarda el search Option tal que:
   * {label: 'section-row-seat', value: 'section'}, de este modo podemos hacer navigateToSearch con el value de la sección. 
   * 
   * Sin embargo, para seleccionar el nodo, en la topview escogeremos el value (para seleccionar la sección) y en el seatmap
   * cogeremos la label para seleccionar el nodo exacto.
   * 
   * @returns {void}
   */
  private selectSearchedSection(): void {

    // Si no hay valor de búsqueda, no hacemos nada.
    if(!this.searchValue()) return;
    
    const currentView:      AppViews | null = this.view(),              
          searchType:       SearchTypes     = this.searchConfig().type; 
    let   selectedSearch!:  string; 

    // Si el buscador es avanzado, seleccionamos el valor de búsqueda en función de la vista. 
    if(searchType === SearchTypes.Advanced) {

      // Si estamos en Topview, seleccionamos la sección buscada (value), si no cogemos el nodo exacto (label).
      selectedSearch = currentView === AppViews.Topview ? this.searchValue()!.value : this.searchValue()!.label!;

    } 
    // Si no, seleccionamos el value.
    else {

      selectedSearch = this.searchValue()!.value!;

    }

    this.dvmService.viewer.select(selectedSearch);

  }

  /**
   * Efecto que reacciona al cambio de vista del DVM.
   * Si la vista es Topview, selecciona la sección buscada.
   */
  public onViewChange: EffectRef = effect((): void => {
  
      const view: AppViews | null = this.view();
  
      if(view === null || !this.searchValue()) return;
  
      switch(view) {
  
        case AppViews.Topview:

         this.selectSearchedSection();

        break;
  
      }
  
  
  });
  
}
